import axios from 'axios';
import { Form, Formik, FormikErrors, FormikHelpers, FormikProps, FormikTouched } from 'formik';
import React, { FC, useState } from 'react';
import { SEND_RECEIPT } from '../../constants/paths/api';
import { CLIENT_ID } from '../../helpers/mobizHelpers/configurations';
import ISendReceipt from '../../interfaces/sendReceipt/sendReceiptMultistep';
import ErrorMessage from './ErrorMessage';
import { MultiStepFormProvider } from './context/multistepContext';
import { TranslationsContext, TranslationsProvider } from './context/translationsContext';
import { sendReceiptInitialValues, sendReceiptSchema } from './sendReceiptMultistep.schema';
import Delivery from './steps/Delivery';
import InstructionToStaff, { SEPARATOR_CHAR } from './steps/InstructionToStaff/InstructionToStaff';
import Received from './steps/Received';
import Recipient from './steps/Recipient';
import Shipment from './steps/Shipment';
import Terms from './steps/Terms';
import TrackingNumbers from './steps/TrackingNumbers/TrackingNumbers';

const getFormData = (values: ISendReceipt): FormData => {
	const formData = new FormData();
	const { files, ...jsonObject } = values;

	switch (jsonObject.instructionsToStaff) {
		case 'hasInvoice':
			formData.append('InstructionsForHandling', '0');
			formData.append('AdditionalInformation', jsonObject.additionalInformation);
			break;
		case 'openUp':
			formData.append('InstructionsForHandling', '1');
			formData.append(
				'AdditionalInformation',
				jsonObject.openUp + SEPARATOR_CHAR + jsonObject.additionalInformation
			);
			break;
		case 'alreadyCleared':
			formData.append('InstructionsForHandling', '3');
			formData.append(
				'AdditionalInformation',
				jsonObject.clearedTrackingNumber + SEPARATOR_CHAR + jsonObject.additionalInformation
			);
			break;
		case 'gift':
			formData.append('InstructionsForHandling', '2');
			formData.append(
				'AdditionalInformation',
				jsonObject.giftType.value + SEPARATOR_CHAR + jsonObject.additionalInformation
			);
			break;
		case 'other':
			formData.append('InstructionsForHandling', '3');
			formData.append('AdditionalInformation', jsonObject.additionalInformation);
			break;
		default:
			formData.append('InstructionsForHandling', '0');
			formData.append('AdditionalInformation', jsonObject.additionalInformation);
			break;
	}

	files.forEach(file => {
		formData.append('Documents', file);
	});

	formData.append('DeliveryMethod', jsonObject.deliveryService);
	formData.append('IdentificationNumber', jsonObject.identificationNumber);
	formData.append('Name', jsonObject.name);
	formData.append('Phone', jsonObject.phone);
	formData.append('Email', jsonObject.email);

	return formData;
};

const getRequestUrl = (values: ISendReceipt): string => {
	let requestUrl = SEND_RECEIPT;
	values.trackingNumbers.forEach(
		(trackingNumber, i) => (requestUrl += `${i === 0 ? '?shipmentIdsList=' : '&shipmentIdsList='}${trackingNumber}`)
	);
	return requestUrl;
};

export interface FormStepProps {
	values: ISendReceipt;
	touched: FormikTouched<ISendReceipt>;
	setFieldTouched: (field: string, value?: boolean, shouldValidate?: boolean) => void;
	setFieldValue: (field: string, value: any) => void;
	errors: FormikErrors<ISendReceipt>;
	resetForm: FormikHelpers<ISendReceipt>['resetForm'];
}

const formSteps: readonly React.FC<FormStepProps>[] = [
	TrackingNumbers,
	Recipient,
	Shipment,
	InstructionToStaff,
	Delivery,
	Terms,
] as const;

interface Props {
	translations?: TranslationsContext;
}

const SendReceiptMultiStep: FC<Props> = ({ translations }) => {
	const [formStatus, setFormStatus] = useState<{ submitted: boolean; errorMessage: string }>({
		submitted: false,
		errorMessage: '',
	});

	const onSubmit = (values: ISendReceipt, actions) => {
		const formData = getFormData(values);
		const requestUrl = getRequestUrl(values);

		axios
			.post(requestUrl, formData, {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					'x-clientId': CLIENT_ID,
				},
			})
			.then(({ data }) => {
				actions.setSubmitting(false);
				setFormStatus({ submitted: true, errorMessage: '' });
				if (!data.error) window.scrollTo(0, 0);
			})
			.catch(e => {
				actions.setSubmitting(false);
				const errorMessage =
					e && e.response && e.response.data && e.response.data.details ? e.response.data.details : e.message;
				setFormStatus({ submitted: true, errorMessage });
			});
	};

	if (formStatus.submitted && !formStatus.errorMessage) {
		return (
			<TranslationsProvider translations={translations}>
				<Received />
			</TranslationsProvider>
		);
	}

	return (
		<>
			{(!formStatus.submitted || (formStatus.submitted && formStatus.errorMessage)) && (
				<Formik
					initialValues={sendReceiptInitialValues}
					validationSchema={sendReceiptSchema}
					onSubmit={onSubmit}>
					{({
						values,
						touched,
						setFieldTouched,
						errors,
						setFieldValue,
						resetForm,
					}: FormikProps<ISendReceipt>) => {
						return (
							<TranslationsProvider translations={translations}>
								<MultiStepFormProvider formStepsLength={formSteps.length}>
									<Form>
										{formSteps.map((Component, index) => {
											return (
												<Component
													key={index}
													values={values}
													touched={touched}
													setFieldTouched={setFieldTouched}
													setFieldValue={setFieldValue}
													errors={errors}
													resetForm={resetForm}
												/>
											);
										})}
									</Form>
								</MultiStepFormProvider>
							</TranslationsProvider>
						);
					}}
				</Formik>
			)}
			{formStatus.submitted && formStatus.errorMessage && <ErrorMessage errorMessage={formStatus.errorMessage} />}
		</>
	);
};

export default SendReceiptMultiStep;
